import { Link } from 'react-router-dom';
import {
  Info,
  Users,
  Target,
  Heart,
  ArrowRight,
  Mail,
  MessageSquare,
  Eye,
  Compass,
} from 'lucide-react';
import { Button, FeatureIcon } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';
import { StackLayers, DirectConnectionDiagram } from '@/components/charts';
import { mission, vision } from '@/content/strategy';

const values = [
  {
    icon: Users,
    title: 'People First',
    description: 'Empower people, not replace them.',
    gradient: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400',
  },
  {
    icon: Target,
    title: 'Simplicity',
    description: 'Complex problems, simple solutions.',
    gradient: 'from-emerald-500/20 to-teal-500/20',
    iconColor: 'text-emerald-400',
  },
  {
    icon: Heart,
    title: 'Trust',
    description: 'Your data stays yours. Always.',
    gradient: 'from-rose-500/20 to-pink-500/20',
    iconColor: 'text-rose-400',
  },
];

export function AboutPage() {
  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <Info className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">About</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
              Talk, Don't Click.
            </span>
            <br />
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              AI Connects You Directly.
            </span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            The internet is changing. We&apos;re building what comes next.
          </p>
        </div>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-12 md:mb-16">
          <Card variant="highlighted" className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-indigo-400" />
              <p className="text-sm text-indigo-300 uppercase tracking-wider font-medium">Vision</p>
            </div>
            <p className="text-lg sm:text-xl font-bold text-white tracking-tight leading-snug mb-3">
              {vision.statement}
            </p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              {vision.support}
            </p>
          </Card>
          <Card variant="highlighted" className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Compass className="w-5 h-5 text-emerald-400" />
              <p className="text-sm text-emerald-300 uppercase tracking-wider font-medium">Mission</p>
            </div>
            <p className="text-lg sm:text-xl font-bold text-white tracking-tight leading-snug mb-3">
              {mission.statement}
            </p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              {mission.support}
            </p>
          </Card>
        </div>

        <div className="flex justify-center -mt-6 mb-12 md:mb-16">
          <Link to="/vision" className="w-full sm:w-auto">
            <Button variant="outline" size="md" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
              Vision, Positioning &amp; Strategy
            </Button>
          </Link>
        </div>

        <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-8 text-center">
          Why Now
        </h2>

        {/* Thesis blocks */}
        <div className="space-y-6 sm:space-y-8 mb-12 md:mb-16">
          {/* Thesis 1 */}
          <Card variant="elevated" className="p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-3">UI Is Becoming a Conversation</h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              30 years of menus, buttons, and forms.{' '}
              <span className="text-indigo-300 font-medium">That era is ending.</span>{' '}
              Users say what they need — AI acts.
            </p>
          </Card>

          {/* Thesis 2 */}
          <Card variant="elevated" className="p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-3">Search Engines Won&apos;t Send You Traffic</h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
              Search engines are keeping users inside their own ecosystem — answering with AI, never linking out.{' '}
              <span className="text-indigo-300 font-medium">Less traffic. Fewer leads.</span>
            </p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-5">
              <span className="text-slate-100 font-medium">MCP</span> connects your business directly to AI agents.
              No search engine middleman.
            </p>
            <DirectConnectionDiagram />
          </Card>

          {/* Thesis 3 */}
          <Card variant="elevated" className="p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-3">AI Is No Longer a Luxury</h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-5">
              AI assistants used to cost a fortune — only large companies could afford them.{' '}
              <span className="text-indigo-300 font-medium">Not anymore.</span>{' '}
              Now any small business can automate customer conversations 24/7.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="p-3 sm:p-4 rounded-xl bg-slate-700/20 border border-slate-700/50 text-center opacity-70">
                <MessageSquare className="w-5 h-5 text-slate-500 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Before</p>
                <p className="text-[10px] text-slate-600 mt-1">enterprise-only</p>
              </div>
              <div className="p-3 sm:p-4 rounded-xl bg-indigo-900/20 border border-indigo-500/30 text-center shadow-card-glow-indigo">
                <MessageSquare className="w-5 h-5 text-indigo-400 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Now</p>
                <p className="text-[10px] text-indigo-400/60 mt-1">for every business</p>
              </div>
            </div>
          </Card>
        </div>

        <SectionDivider variant="gradient" />

        {/* Values */}
        <div className="my-12 md:my-16">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-8 text-center">
            What We Stand For
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} variant="default" hoverable className="text-center p-6">
                  <FeatureIcon gradient={value.gradient} className="mx-auto mb-4">
                    <Icon className={`w-6 h-6 ${value.iconColor}`} />
                  </FeatureIcon>
                  <h3 className="text-lg font-semibold text-slate-100 tracking-tight mb-1">{value.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* How we build */}
        <div className="my-12 md:my-16">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            How We Build
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            Three commitments, one inside the next. DialogMe runs on its own AI stack, Fnk — inside
            an environment you choose, under terms you control.
          </p>
          <StackLayers />
        </div>

        {/* Contact */}
        <Card variant="elevated" className="mb-12 md:mb-16 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
          <div className="flex-1">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">Get in touch</p>
            <p className="text-sm text-slate-400 leading-relaxed">We build tools for real businesses.</p>
          </div>
          <a href="mailto:sales@dialogme.ai">
            <Button variant="outline" size="md" leftIcon={<Mail className="w-4 h-4" />}>
              sales@dialogme.ai
            </Button>
          </a>
        </Card>

        {/* CTA */}
        <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">Seeing Is Believing</h2>
          <p className="text-slate-300 mb-6 leading-relaxed">No credit card. No setup. Just conversation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Try Live Demo
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}
